<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Complaints"
      subtitle="Review and manage student complaints"
      icon="fas fa-comment-exclamation"
      gradient="orange"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search complaint type or status"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="fStatusFilter"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="selected.status"
            input-id="fStatusFilter"
            :options="statusOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="info"
            :disabled="tableData.busy"
            @click.prevent="showItem(item)"
          >
            View
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(student)="{ item }">
        {{ item.student?.id_number || 'N/A' }}
      </template>

      <template #cell(status)="{ item }">
        <BBadge :variant="statusVariant(item.status)">
          {{ item.status }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalView"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Complaint Details"
    >
      <BRow>
        <BCol
          cols="12"
          class="mb-2"
        >
          <h6 class="font-weight-bolder">
            Complaint Type
          </h6>
          <p class="mb-2">
            {{ activeItem.complaint_type || 'N/A' }}
          </p>
          <h6 class="font-weight-bolder">
            Description
          </h6>
          <p class="mb-2">
            {{ activeItem.description || 'N/A' }}
          </p>
          <h6 class="font-weight-bolder">
            Admin Response
          </h6>
          <p class="mb-2">
            {{ activeItem.admin_response || 'No response yet.' }}
          </p>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Status"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <VSelect
              v-model="selected.updateStatus"
              :options="statusOptions"
              append-to-body
              :calculate-position="calculateDropPosition"
              :disabled="state.busy"
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Attachment"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BButton
              size="sm"
              variant="outline-primary"
              :disabled="!activeItem.attachment || state.busy"
              @click.prevent="viewAttachment(activeItem)"
            >
              View Attachment
            </BButton>
          </BFormGroup>
        </BCol>

        <BCol cols="12">
          <BFormGroup
            label="Admin Response"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormTextarea
              v-model="formStatus.admin_response"
              rows="3"
              :disabled="state.busy"
            />
          </BFormGroup>
        </BCol>
      </BRow>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="updateStatus"
        >
          Update Status
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminComplaint } from '@/services'

export default {
  name: 'VAdminComplaints',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Complaints' }),

  data () {
    return {
      state: { busy: false },
      selected: {
        status: null,
        updateStatus: null
      },
      statusOptions: ['pending', 'reviewing', 'resolved', 'rejected'],
      activeItem: {},
      formStatus: {
        status: '',
        admin_response: ''
      },
      tableData: {
        busy: false,
        filter: {
          search: '',
          status: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'date_reported',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'student', label: 'Student', class: 'min-w-100px' },
          { mobile: 2, key: 'complaint_type', class: 'min-w-150px' },
          { mobile: 3, key: 'status', class: 'text-center min-w-100px' },
          { mobile: 4, key: 'date_reported', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'selected.status' (value) {
      this.tableData.filter.status = value || ''
    },
    'selected.updateStatus' (value) {
      this.formStatus.status = value || ''
    }
  },

  methods: {
    statusVariant (status) {
      const variants = {
        pending: 'warning',
        reviewing: 'info',
        resolved: 'success',
        rejected: 'danger'
      }
      return variants[status] || 'secondary'
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        status: filter.status || ''
      }

      return SAdminComplaint.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    showItem (item) {
      this.activeItem = { ...item }
      this.formStatus = {
        status: item.status,
        admin_response: item.admin_response || ''
      }
      this.selected.updateStatus = item.status
      this.$refs.modalView.show()
    },

    async updateStatus () {
      if (!this.activeItem?.id) return

      this.state.busy = true
      SAdminComplaint.updateStatus(this.activeItem.id, this.formStatus).then(() => {
        this.swalSuccess('Complaint status updated successfully')
        this.$refs.tableData.refetch()
        this.$refs.modalView.hide()
      }).catch(error => {
        this.swalError(error.response?.data?.message || 'Failed to update status')
      }).finally(() => {
        this.state.busy = false
      })
    },

    async viewAttachment (item) {
      this.state.busy = true
      SAdminComplaint.viewAttachment(item.id).then(({ data, headers }) => {
        const mimeType = headers['content-type'] || 'application/octet-stream'
        const blob = new Blob([data], { type: mimeType })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      }).catch(error => {
        this.swalError(error.response?.data?.message || 'Unable to open attachment')
      }).finally(() => {
        this.state.busy = false
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete complaint',
        text: 'Are you sure you want to delete this complaint?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        SAdminComplaint.destroy(item.id).then(() => {
          this.swalSuccess('Complaint deleted successfully')
          this.$refs.tableData.refetch()
        }).catch(error => {
          this.swalError(error.response?.data?.message || 'Failed to delete complaint')
        }).finally(() => {
          this.state.busy = false
        })
      }
    }
  }
}
</script>
